import { LOADING } from "App/Templates/LoadingTemplate";
import { ValidType } from "constants/ValidType";
import { Title } from "GraphQL/Queries/Metadata/GetMetadataTitleRelation";
import { Metadata, Artist, Company, Url } from "GraphQL/Queries/Metadata/GetMetadata";
import { createDuck } from "lib/store/v2";
import { Edge } from "models/Edge";
import { Info } from "App/Atomics/Input/Select/TextSelect";
import uuidv4 from "uuid/v4";
import _ from "lodash";

const createInitialState = () => ({
  edge: null as null | Edge,
  liveReplay: [] as Metadata[],
  origin: [] as Metadata[],
  loading: LOADING.ONLOAD as LOADING,
  editLoading: false as boolean,
  titleRelation: [] as Title[],
  modalId: "" as string
});

export const LiveReplayActions = createDuck({
  namespace: "LiveReplay",
  createInitialState,
  reducers: {
    setLoading(state, loading: LOADING) {
      state.loading = loading;
    },
    setEditLoading(state, loading: boolean) {
      state.editLoading = loading;
    },
    setEdge(state, edge: Edge) {
      state.edge = edge;
    },
    setPodCasts(state, podcasts: Metadata[]) {
      state.liveReplay = podcasts;
      state.origin = podcasts;
    },

    deletePodCast(state, index: number) {
      state.liveReplay.splice(index, 1);
    },
    setPodCastClear(state) {
      state.liveReplay = state.origin;
    },
    setPodCastSave(state) {
      state.origin = state.liveReplay;
    },
    updatePodCastTitle(state, { index, title }: { index: number; title: string }) {
      state.liveReplay[index].title = title;
    },
    updatePodCastVC(state, { index, vc }: { index: number; vc: ValidType }) {
      state.liveReplay[index].validCheck = vc;
    },
    createPodCastImage(
      state,
      { index, uuid, typeUrl, url, order }: { index: number; uuid: string; typeUrl: string; url: string; order: number }
    ) {
      if (!state.liveReplay[index].metadataUrl.length) {
        state.liveReplay[index].metadataUrl = [{ uuid, typeUrl, url, validCheck: ValidType.V3, order }];
      } else {
        state.liveReplay[index].metadataUrl.push({
          uuid,
          typeUrl,
          url,
          validCheck: ValidType.V3,
          order
        });
      }
    },
    updatePodCastImage(state, { index, uuid, typeUrl, url }: { index: number; uuid: string; typeUrl: string; url: string }) {
      const urlIndex = state.liveReplay[index].metadataUrl.findIndex((url: any) => url.uuid === uuid);
      state.liveReplay[index].metadataUrl[urlIndex].typeUrl = typeUrl;
      state.liveReplay[index].metadataUrl[urlIndex].url = url;
    },
    createPodCastArtist(state, index: number) {
      const { artistRelation } = state.liveReplay[index];
      const uuid = uuidv4();
      const order = !artistRelation.length ? 0 : _.last(artistRelation)!.order + 1;
      const tempArtist = {
        uuid,
        artist: [{ id: undefined, name: undefined }],
        role: [{ id: undefined, name: undefined }],
        character: [{ id: undefined, name: undefined }],
        validCheck: ValidType.V3,
        order
      } as Artist;
      if (!artistRelation.length) {
        state.liveReplay[index].artistRelation = [tempArtist];
      } else {
        state.liveReplay[index].artistRelation.push(tempArtist);
      }
    },
    updatePodCastArtist(state, { index, artistIndex, info }: { index: number; artistIndex: number; info: Info }) {
      const { artistRelation } = state.liveReplay[index];
      artistRelation[artistIndex].artist[0].id = info.id;
      artistRelation[artistIndex].artist[0].name = info.name;
    },
    updatePodCastArtistUuid(state, { index, prevUuid, uuid }: { index: number; prevUuid: string; uuid: string }) {
      const { artistRelation } = state.liveReplay[index];
      const artistIndex = artistRelation.findIndex(({ uuid }) => uuid === prevUuid);
      artistRelation[artistIndex].uuid = uuid;
    },
    updatePodCastRole(state, { index, roleIndex, info }: { index: number; roleIndex: number; info: Info }) {
      const { artistRelation } = state.liveReplay[index];
      artistRelation[roleIndex].role[0].id = info.id;
      artistRelation[roleIndex].role[0].name = info.name;
    },
    updatePodCastCharacter(state, { index, characterIndex, info }: { index: number; characterIndex: number; info: Info }) {
      const { artistRelation } = state.liveReplay[index];
      if (!artistRelation[characterIndex].character.length) {
        artistRelation[characterIndex].character = [{ id: info.id, name: info.name }];
      } else {
        artistRelation[characterIndex].character[0].id = info.id;
        artistRelation[characterIndex].character[0].name = info.name;
      }
    },
    deletePodCastArtist(state, { index, artistIndex }: { index: number; artistIndex: number }) {
      state.liveReplay[index].artistRelation.splice(artistIndex, 1);
    },
    setPodCastRightsCompany(state, { index, id, name }: { index: number; id: string; name: string }) {
      state.liveReplay[index].rightsCompany[0].company[0].id = id;
      state.liveReplay[index].rightsCompany[0].company[0].name = name;
    },
    setPodCastProductions(state, { index, productions }: { index: number; productions: Company[] }) {
      state.liveReplay[index].productions = productions;
    },
    setPodCastPublications(state, { index, publications }: { index: number; publications: Company[] }) {
      state.liveReplay[index].publications = publications;
    },
    updatePodCastCompanyUuid(
      state,
      { index, typeKind, prevUuid, uuid }: { index: number; typeKind: string; prevUuid: string; uuid: string }
    ) {
      if (typeKind === "productions") {
        const companyIndex = state.liveReplay[index].productions.findIndex(({ uuid }) => uuid === prevUuid);
        state.liveReplay[index].productions[companyIndex].uuid = uuid;
      } else {
        const companyIndex = state.liveReplay[index].publications.findIndex(({ uuid }) => uuid === prevUuid);
        state.liveReplay[index].publications[companyIndex].uuid = uuid;
      }
    },

    createPodCastUrl(state, { index, url }: { index: number; url: Url }) {
      if (!state.liveReplay[index].metadataUrl.length) {
        state.liveReplay[index].metadataUrl = [url];
      } else {
        state.liveReplay[index].metadataUrl.push(url);
      }
    },
    updatePodCastTypeUrl(state, { index, urlIndex, typeUrl }: { index: number; urlIndex: number; typeUrl: string }) {
      const order = (_.last(state.liveReplay[index].metadataUrl.filter(item => item.typeUrl === typeUrl))?.order ?? -1) + 1;
      state.liveReplay[index].metadataUrl[urlIndex].typeUrl = typeUrl;
      state.liveReplay[index].metadataUrl[urlIndex].order = order;
    },
    updatePodCastUrl(state, { index, urlIndex, url }: { index: number; urlIndex: number; url: string }) {
      state.liveReplay[index].metadataUrl[urlIndex].url = url;
    },
    updatePodCastUrlUuid(state, { index, prevUuid, uuid }: { index: number; prevUuid: string; uuid: string }) {
      const { metadataUrl } = state.liveReplay[index];
      const urlIndex = metadataUrl.findIndex(({ uuid }) => uuid === prevUuid);
      metadataUrl[urlIndex].uuid = uuid;
    },
    deletePodCastUrl(state, { index, urlIndex }: { index: number; urlIndex: number }) {
      state.liveReplay[index].metadataUrl.splice(urlIndex, 1);
    },

    setPodCastTitleRelation(state, titleRelation: Title[]) {
      state.titleRelation = titleRelation;
    },
    createPodCastTitleRelation(
      state,
      {
        uuid,
        typeTitle,
        value,
        languageCode,
        order
      }: { uuid: string; typeTitle: string; value: string; languageCode: string; order: number }
    ) {
      const newTitle = {
        uuid,
        typeTitle,
        value,
        validCheck: ValidType.V3,
        order,
        language: [
          {
            languageCode
          }
        ]
      };
      state.titleRelation.push(newTitle);
    },
    updatePodCastTitleRelation(
      state,
      { index, typeTitle, value, languageCode }: { index: number; typeTitle: string; value: string; languageCode: string }
    ) {
      state.titleRelation[index].typeTitle = typeTitle;
      state.titleRelation[index].value = value;
      state.titleRelation[index].language[0].languageCode = languageCode;
    },
    deletePodCastTitleRelation(state, uuid: string) {
      const index = state.titleRelation.findIndex(title => title.uuid === uuid);
      state.titleRelation.splice(index, 1);
    },
    setPodCastTitleRelationModalId(state, uuid: string) {
      state.modalId = uuid;
    }
  }
});
